import ASTExplorer from "./badgersense/ast_explorer";
import { containsNonIdentChars } from "./utils";

const keyMaps = {
  arrowKeys: new Set(["ArrowUp", "ArrowDown"]),
  movementKeys: new Set([
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
    "PageUp",
    "PageDown",
  ]),
  stringChars: new Set(["'", '"', "`"]),
  wrappingJumps: {
    ")": "(",
    "]": "[",
    "}": "{",
    "/": "/",
    "'": "'",
    '"': '"',
  },
  wrappingPairs: {
    "(": ")",
    "[": "]",
    "{": "}",
    "/": "/",
    "'": "'",
    '"': '"',
  },
  wrappingChars: new Set(["'", '"', "(", ")", "[", "]", "{", "}", "/"]),
};

export default class QueryInputHandler {
  constructor() {
    this.undoStack = [];
    this.undoIndex = -1;
  }

  addUndoState(query, cursorPos) {
    this.undoStack.splice(this.undoIndex + 1);
    this.undoStack.push([query, cursorPos]);
    this.undoIndex++;
  }

  undo() {
    if (this.undoIndex > 0) {
      this.undoIndex--;
      return this.undoStack[this.undoIndex];
    }
    return null;
  }

  redo() {
    if (this.undoIndex < this.undoStack.length - 1) {
      this.undoIndex++;
      return this.undoStack[this.undoIndex];
    }
    return null;
  }

  checkForUndoPush(ret) {
    if (ret.preventDefault && !ret.noUndo) {
      this.addUndoState(ret.query, ret.cursorPos);
    }
  }

  handle(opts) {
    const { query, isSuggesting, key, metaKey, shiftKey, ctrlKey, altKey } =
      opts;

    let ret = { query, calls: [] };

    // Closing suggestions
    //
    if (isSuggesting && key === "Escape") {
      ret.calls.push("removeSuggestions");
      //
      // Opening suggestions
      //
    } else if (key == "Escape") {
      ret.calls.push("updateSuggestions");
      //
      // Commenting out a BadgerQL function
      //
    } else if (key === "/" && metaKey) {
      this.handleComment(ret, opts);
      //
      // Close the docs
      //
    } else if (key === "." && metaKey && shiftKey) {
      ret.preventDefault = true;
      ret.noUndo = true;
      ret.calls.push("closeDocs");
      //
      // Open the docs!
      //
    } else if (key === "." && metaKey) {
      this.handleOpeningDocs(ret, opts);
      //
      // Submitting a query
      //
    } else if (key === "Enter" && (metaKey || ctrlKey)) {
      ret.calls.push(...["submitQuery", "removeSuggestions"]);
      //
      // Opening or closing nested blocks (parentheses, brackets, etc)
      //
    } else if (keyMaps.wrappingChars.has(key)) {
      ret.preventDefault = true;
      this.handleWrappingChar(ret, opts);
      //
      // Backspacing and removing paired blocks
      //
    } else if (key == "Backspace" && !metaKey && !ctrlKey && !altKey) {
      ret.preventDefault = true;
      this.handleBackspace(ret, opts);
      //
      // Tab
      //
    } else if (key == "Tab") {
      ret.preventDefault = true;
      this.handleTab(ret, opts);
      //
      // Suggestion completion
      //
    } else if (isSuggesting && key == "Enter" && (!shiftKey || !altKey)) {
      ret.preventDefault = true;
      this.insertFromSuggestion(ret, opts);
      ret.calls.push("reloadQueryArea", "onQueryChange");
      //
      // Suggestion arrow keys
      //
    } else if (isSuggesting && keyMaps.arrowKeys.has(key)) {
      ret.preventDefault = true;
      ret.noUndo = true;
      if (key === "ArrowUp") {
        ret.calls.push("moveSelectionUp");
      } else {
        ret.calls.push("moveSelectionDown");
      }
      //
      // Movement keys
      //
    } else if (keyMaps.movementKeys.has(key)) {
      ret.calls.push(...["reloadQueryArea", "updateSuggestions"]);
      //
      // Undo
      //
    } else if ((metaKey || ctrlKey) && key === "z") {
      ret.preventDefault = true;
      ret.noUndo = true;
      const [prev, prevCursor] = this.undo();
      if (prev !== null) {
        ret.query = prev;
        ret.cursorPos = prevCursor;
        ret.calls.push("onQueryChange");
      }
      ret.calls.push(...["reloadQueryArea", "updateSuggestions"]);
      //
      // Redo
      //
    } else if ((metaKey || ctrlKey) && key === "y") {
      ret.preventDefault = true;
      ret.noUndo = true;
      const [next, nextCursor] = this.redo();
      if (next !== null) {
        ret.query = next;
        ret.cursorPos = nextCursor;
        ret.calls.push("onQueryChange");
      }
      ret.calls.push(...["reloadQueryArea", "updateSuggestions"]);
    } else {
      return null;
    }

    this.checkForUndoPush(ret);

    return ret;
  }

  handleOpeningDocs(ret, opts) {
    const { selection, badgersenseMeta } = opts;

    if (badgersenseMeta === undefined) return;

    const lookup = (kind, token) => {
      if (badgersenseMeta[kind] && badgersenseMeta[kind][token]) {
        return true;
      }

      return false;
    };

    const token = selection?.token;

    if (selection?.type === "token") {
      if (!lookup("badgerql_functions", token)) return;
      ret.calls.push(["docLinkSelect", token]);
    } else if (selection?.type === "exprFn") {
      if (!lookup("expression_functions", token)) return;
      ret.calls.push(["docLinkSelect", `expr-${token}`]);
    } else if (selection?.parent?.type === "exprFn") {
      if (!lookup("expression_functions", selection.parent.token)) return;
      ret.calls.push(["docLinkSelect", `expr-${selection.parent.token}`]);
    }

    ret.preventDefault = true;
    ret.noUndo = true;
  }

  handleTab(ret, opts) {
    const { start: selStart, end: selEnd, query, isSuggesting, altKey } = opts;

    if (isSuggesting && !altKey) {
      const suggestion = opts.getSuggestion();
      if (suggestion?.rest === undefined) return;

      const split = suggestion.rest.split(".");
      if (split.length > 1) {
        const start = query.slice(0, selStart);
        const end = query.slice(selStart);
        ret.query = start + split[0] + "." + end;
        ret.cursorPos = selStart + split[0].length + 1;
        ret.calls.push("updateSuggestions");
      } else {
        this.insertFromSuggestion(ret, opts, suggestion);
      }
    } else {
      let before = query.slice(0, selStart);
      let after = query.slice(selEnd);
      ret.query = before + "  " + after;
      ret.cursorPos = selStart + 2;
      ret.calls.push("updateSuggestions");
    }

    ret.calls.push(...["reloadQueryArea", "onQueryChange"]);
  }

  handleBackspace(ret, opts) {
    const { start, end, query } = opts;

    if (start === 0 && end === 0) {
      return;
    } else if (start === end) {
      const removed = query.slice(start - 1, end);
      const next = query.slice(start, end + 1);
      const pair = keyMaps.wrappingPairs[removed];

      if (pair && pair === next) {
        ret.query = query.slice(0, start - 1) + query.slice(end + 1);
        ret.cursorPos = start - 1;
      } else {
        ret.query = query.slice(0, start - 1) + query.slice(end);
      }

      ret.cursorPos = start - 1;
    } else {
      ret.query = query.slice(0, start) + query.slice(end);
      ret.cursorPos = start;
    }

    ret.calls.push(
      ...["reloadQueryArea", ["updateSuggestions", true], "onQueryChange"],
    );
  }

  handleComment(ret, opts) {
    let before = opts.query.slice(0, opts.start);
    let barPos = before.lastIndexOf("|");
    let bangPos = before.lastIndexOf("!");
    if (barPos === -1 && bangPos === -1) {
      this.insertIntoQuery(ret, opts, "!", 0);
    } else if (bangPos >= barPos) {
      ret.query =
        opts.query.slice(0, bangPos) +
        opts.query.slice(bangPos + 1, opts.query.length);
      ret.cursorPos = opts.start - 1;
    } else {
      this.insertIntoQuery(ret, opts, "!", barPos + 2);
    }

    ret.calls.push("reloadQueryArea");
  }

  withinSelectionContext(key, selection) {
    if (selection?.outside) return false;

    if (key === ")") {
      return (
        (selection?.type === "exprFn" && !selection?.open) ||
        (selection?.type === "group" && !selection?.open) ||
        (selection?.parent?.type === "exprFn" && !selection?.parent?.open) ||
        (selection?.parent?.type === "group" && !selection?.parent?.open)
      );
    } else if (keyMaps.stringChars.has(key)) {
      return selection?.type === "string" && selection?.stringChar === key;
    } else if (key === "}") {
      return selection?.type === "block" && !selection?.open;
    }
  }

  shouldWrap(key, selection) {
    if (key === "/") {
      return (
        ASTExplorer.rootFnToken(selection)?.token === "parse" ||
        (selection.children &&
          selection.children[selection.children?.length - 1]?.token === "match")
      );
    }

    if (selection?.type === "regex") {
      return false;
    }

    if (keyMaps.stringChars.has(key)) {
      return !(selection?.type === "string" && selection?.stringChar !== key);
    }

    return true;
  }

  handleWrappingChar(ret, opts) {
    const { start: selStart, query, key, selection } = opts;

    const start = query.slice(0, selStart);
    const end = query.slice(selStart);

    const prev = query.slice(selStart - 1, selStart);
    const next = query.slice(selStart, selStart + 1);

    if (prev === "\\") {
      ret.query = start + key + end;
    } else if (key === next && this.withinSelectionContext(key, selection)) {
      // Just move the cursor forward
    } else if (keyMaps.wrappingPairs[key] && this.shouldWrap(key, selection)) {
      if (selection?.type === "token" && selection?.idxOffset === 0) {
        const rest = end.slice(selection.token.length);
        ret.query =
          start + key + selection.token + keyMaps.wrappingPairs[key] + rest;
      } else if (selection?.type === "string" && selection?.idxOffset === 0) {
        const { stringChar, contents } = selection;
        const rest = end.slice(contents.length + 2);
        ret.query =
          start +
          key +
          stringChar +
          contents +
          stringChar +
          keyMaps.wrappingPairs[key] +
          rest;
      } else if (selection?.type === "exprFn" && key === "(") {
        const wrapExpr = query.slice(
          selection.start + selection.idxOffset,
          selection.end + 1,
        );
        const after = query.slice(selection.end + 1);
        ret.query = start + "(" + wrapExpr + ")" + after;
      } else {
        ret.query = start + key + keyMaps.wrappingPairs[key] + end;
      }
    } else {
      ret.query = start + key + end;
    }

    ret.cursorPos = selStart + 1;
    ret.calls.push(
      ...["reloadQueryArea", "updateSuggestions", "onQueryChange"],
    );
  }

  insertFromSuggestion(ret, opts, suggestion = null) {
    let { kind, type, matched, infix, rest, spaceAfter } =
      suggestion || opts.getSuggestion();
    const { query, start: selStart, selection } = opts;

    const start = query.slice(0, selStart);
    const end = query.slice(selStart);

    if (kind === "exprFn" && selection?.type === "token" && !infix) {
      const { token, idxOffset } = selection;
      const postToken = token.slice(idxOffset);
      const after = end.slice(postToken.length);
      ret.query = start + rest + "(" + postToken + ")" + after;
      ret.cursorPos = rest.length + selStart + 1;
    } else if (kind === "exprFn" && selection?.type === "string" && !infix) {
      const { stringChar, contents } = selection;
      const after = end.slice(contents.length + 2);
      ret.query =
        start + rest + "(" + stringChar + contents + stringChar + ")" + after;
      ret.cursorPos = rest.length + selStart + 1;
    } else if (kind === "exprFn" && selection?.type === "exprFn") {
      const wrapExpr = query.slice(
        selection.start + selection.idxOffset,
        selection.end + 1,
      );
      const after = query.slice(selection.end + 1);
      ret.query = start + rest + "(" + wrapExpr + ")" + after;
      ret.cursorPos = rest.length + selStart + 1;
    } else if (kind === "exprFn" && !infix) {
      ret.query = start + rest + "()" + end;
      ret.cursorPos = rest.length + selStart + 1;
    } else if (kind === "field") {
      if (containsNonIdentChars(matched + rest)) {
        const removed = query.slice(0, start.length - matched.length);
        const field = "`" + matched + rest + "`::" + type;
        ret.query = removed + field + end;
        ret.cursorPos = field.length + (selStart - matched.length);
      } else {
        const field = rest + "::" + type;
        ret.query = start + field + end;
        ret.cursorPos = field.length + selStart;
      }
    } else {
      if (spaceAfter || infix === true) rest += " ";
      ret.cursorPos = rest.length + selStart;
      ret.query = start + rest + end;
    }

    if (kind !== "regex") {
      ret?.calls?.push("removeSuggestions");
    }
  }

  insertIntoQuery(ret, opts, str, pos) {
    const { query, start, end } = opts;
    let before, after;

    if (pos === 0) {
      before = "";
      after = query;
    } else if (pos) {
      before = query.slice(0, pos);
      after = query.slice(pos, query.length);
    } else {
      before = query.slice(0, start);
      after = query.slice(end, query.length);
    }

    ret.query = before + str + after;
    ret.cursorPos = start + str.length;
  }
}
